import React, { useState, useCallback } from 'react';
import { Box, Slider, Typography, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { RiskAssessment } from '../types/types';

interface RiskAssessmentSliderProps {
  value: RiskAssessment;
  onChange: (value: RiskAssessment) => void;
}

const marks = [
  { value: 0, label: 'Conservative' },
  { value: 50, label: 'Realistic' },
  { value: 100, label: 'Optimistic' },
];

const valueToRisk = (v: number): RiskAssessment => {
  if (v <= 25) return 'Conservative';
  if (v >= 75) return 'Optimistic';
  return 'Realistic';
};

const riskToValue = (r: RiskAssessment): number => {
  switch (r) {
    case 'Conservative':
      return 0;
    case 'Optimistic':
      return 100;
    case 'Realistic':
    default:
      return 50;
  }
};

const RiskAssessmentSlider: React.FC<RiskAssessmentSliderProps> = ({ value, onChange }) => {
  // Use local numeric state for immediate responsiveness
  const [localValue, setLocalValue] = useState(riskToValue(value));
  
  // Debounced onChange to avoid excessive updates
  const debouncedOnChange = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (newValue: number) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          const riskAssessment = valueToRisk(newValue);
          onChange(riskAssessment);
        }, 100); // Small delay to batch rapid changes
      };
    })(),
    [onChange]
  );

  const handleChange = (event: Event, newValue: number | number[]) => {
    const numericValue = Array.isArray(newValue) ? newValue[0] : newValue;
    setLocalValue(numericValue); // Update local state immediately for responsiveness
    debouncedOnChange(numericValue); // Debounce the actual onChange call
  };

  const handleChangeCommitted = (event: Event | React.SyntheticEvent, newValue: number | number[]) => {
    const numericValue = Array.isArray(newValue) ? newValue[0] : newValue;
    const riskAssessment = valueToRisk(numericValue);
    onChange(riskAssessment); // Immediate update when user finishes dragging
  };

  // Sync local state when prop changes
  React.useEffect(() => {
    setLocalValue(riskToValue(value));
  }, [value]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 1 }}>Risk Assessment</Typography>
        <Tooltip title="Risk adjustment accounts for market uncertainties and execution challenges." aria-label="Risk adjustment explanation">
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Box>
      <Slider
        value={localValue}
        onChange={handleChange}
        onChangeCommitted={handleChangeCommitted}
        step={1}
        marks={marks}
        min={0}
        max={100}
        aria-label="Risk Assessment slider"
        sx={{
          '& .MuiSlider-thumb': {
            cursor: 'grab',
            transition: 'transform 0.1s ease',
            '&:active': {
              cursor: 'grabbing',
              transform: 'scale(1.2)',
            },
            '&:hover': {
              transform: 'scale(1.1)',
            }
          },
          '& .MuiSlider-track': {
            cursor: 'pointer',
            transition: 'all 0.1s ease',
          },
          '& .MuiSlider-rail': {
            cursor: 'pointer',
          },
          '& .MuiSlider-mark': {
            cursor: 'pointer',
            transition: 'all 0.1s ease',
            '&:hover': {
              transform: 'scale(1.2)',
            }
          },
          // Smooth color transitions
          '& .MuiSlider-valueLabel': {
            transition: 'all 0.1s ease',
          }
        }}
        // Performance optimizations
        disableSwap
        valueLabelDisplay="auto"
        valueLabelFormat={(value) => valueToRisk(value)}
      />
    </Box>
  );
};

export default RiskAssessmentSlider;


